import { useMemo, useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  useGetOrgRequirementsByOrgLazyQuery,
  useAddAcademicYearMutation,
  useAddAcademicYearTagMutation,
  useAddAcademicYearRequirementMutation,
  useAddGraduationClassMutation,
  useAddGraduationClassStudentMutation,
  useGetProgramsByOrgLazyQuery,
  useGetUsersRoleByOrgLazyQuery,
  useAddMentoringMutation,
  useGetOrganizationByIdLazyQuery,
} from "graphql/__generated__/hooks";
import { Requirement } from "types/requirement";
import { Tag } from "types/tag";
import { useAuthUser } from "hooks/useAuthUser";
import { ROLES, User } from "types/user";
import { useNotifications } from "hooks/useNotifications";
import { NotificationSeverity } from "helpers/constants";
import { ORG_TYPE } from "types/organization";

export const useOnboardingStudent = () => {
  const { organizationId } = useParams();
  const { user } = useAuthUser();
  const { notify } = useNotifications();
  const [getUsersRoleByOrg, { data: usersRoleByOrgData }] =
    useGetUsersRoleByOrgLazyQuery();
  const [getOrganizationById, { data: organizationData }] =
    useGetOrganizationByIdLazyQuery();

  // stepper state
  const [activeStep, setActiveStep] = useState(0);

  // data states
  const [availableRequirements, setAvailableRequirements] = useState<
    Requirement[]
  >([]);
  const [selectedRequirements, setSelectedRequirements] = useState<
    Requirement[]
  >([]);

  // validation state for step 1
  const [requirementsError, setRequirementsError] = useState<string | null>(
    null,
  );

  // tags state and helpers
  const [tags, setTags] = useState<Tag[]>([]);
  const [programId, setProgramId] = useState<string>();

  // coaches selection state
  const [selectedCoaches, setSelectedCoaches] = useState<User[]>([]);

  // load requirements by organization
  const [getOrgRequirements, { data: orgReqData, loading }] =
    useGetOrgRequirementsByOrgLazyQuery();
  const [getProgramsByOrg, { data: programData }] =
    useGetProgramsByOrgLazyQuery();

  const [addAcademicYear] = useAddAcademicYearMutation();
  const [addAcademicYearTag] = useAddAcademicYearTagMutation();
  const [addAcademicYearRequirement] = useAddAcademicYearRequirementMutation();
  const [addGraduationClass] = useAddGraduationClassMutation();
  const [addGraduationClassStudent] = useAddGraduationClassStudentMutation();
  const [addMentoring] = useAddMentoringMutation();

  const organization = organizationData?.organization_by_pk;
  const mentorsOptional = organization?.type === ORG_TYPE.EDUCATIONAL;

  const coaches = useMemo(() => {
    return usersRoleByOrgData?.organization_user.map((user) => user.user) || [];
  }, [usersRoleByOrgData]);

  const loadRequirements = useCallback(() => {
    if (!organizationId) return;
    getOrgRequirements({ variables: { orgId: organizationId } });
    getProgramsByOrg({ variables: { orgId: organizationId } });
    getUsersRoleByOrg({
      variables: { orgId: organizationId, role: ROLES.COACH },
    });
    getOrganizationById({ variables: { organizationId } });
  }, [
    organizationId,
    getOrgRequirements,
    getProgramsByOrg,
    getUsersRoleByOrg,
    getOrganizationById,
  ]);

  useEffect(() => {
    loadRequirements();
  }, [loadRequirements]);

  useEffect(() => {
    if (orgReqData?.organization_requirement) {
      const reqs = (orgReqData.organization_requirement || []).map(
        (r) => r.requirement,
      ) as Requirement[];
      setAvailableRequirements(reqs);
    }
  }, [orgReqData]);

  // clear error when selection becomes valid
  useEffect(() => {
    if (selectedRequirements.length > 0 && requirementsError) {
      setRequirementsError(null);
    }
  }, [selectedRequirements, requirementsError]);

  useEffect(() => {
    const progId = programData?.program?.[0]?.program_id as string | undefined;
    if (progId) {
      setProgramId(progId);
    }
  }, [programData]);

  // selection handlers
  const handleAddRequirement = (requirement: Requirement) => {
    setSelectedRequirements((prev) => {
      if (prev.find((r) => r.requirement_id === requirement.requirement_id)) {
        return prev;
      }
      return [...prev, requirement];
    });
  };

  const handleRemoveRequirement = (requirementId: string) => {
    setSelectedRequirements((prev) =>
      prev.filter((r) => r.requirement_id !== requirementId),
    );
  };

  // tag helpers
  const handleAddTag = (tag: Tag) => setTags((prev) => [...prev, tag]);
  const handleRemoveTag = (tag: Tag) =>
    setTags((prev) => prev.filter((t) => t.tag_id !== tag.tag_id));

  // coaches handlers
  const handleAddCoach = (coach: User) =>
    setSelectedCoaches((prev) => {
      if (prev.find((c) => c.user_id === coach.user_id)) return prev;
      return [...prev, coach];
    });
  const handleRemoveCoach = (coachId: string) =>
    setSelectedCoaches((prev) => prev.filter((c) => c.user_id !== coachId));
  const handleChangeCoaches = (users: User[]) => setSelectedCoaches(users);

  // navigation
  const nextStep = () => setActiveStep((s) => Math.min(s + 1, 2));
  const previousStep = () => setActiveStep((s) => Math.max(s - 1, 0));
  const goToStep = (idx: number) =>
    setActiveStep(Math.min(Math.max(idx, 0), 2));

  const goNextFromRequirements = () => {
    if (selectedRequirements.length === 0) {
      setRequirementsError("Please select at least one requirement.");
      return;
    }
    nextStep();
  };

  const state = useMemo(
    () => ({
      activeStep,
      organizationId: organizationId || null,
    }),
    [activeStep, organizationId],
  );

  const handleComplete = async (params: {
    name: string;
    start_date: string; // ISO yyyy-MM-dd
    end_date: string; // ISO yyyy-MM-dd
  }) => {
    if (
      !programId ||
      !selectedRequirements ||
      selectedRequirements.length === 0 ||
      (!mentorsOptional && selectedCoaches.length === 0)
    ) {
      // avoid continue if any of the required fields are not filled
      notify({
        message: "Please fill all the required fields",
        severity: NotificationSeverity.error,
      });
      return;
    }

    // 1) Create AcademicYear
    const academicYearName = `${params.name} - ${user?.name ?? "User"}`;
    const ayResult = await addAcademicYear({
      variables: {
        name: academicYearName,
        start_date: params.start_date,
        end_date: params.end_date,
        program_id: programId,
      },
    });
    const academicYearId = ayResult.data?.insert_academic_year?.returning?.[0]
      ?.academic_year_id as string | undefined;
    if (!academicYearId) return;

    // 1.1) Add AcademicYear Tags
    if (tags.length > 0) {
      for (const tag of tags) {
        await addAcademicYearTag({
          variables: { academicYearId, tagId: tag.tag_id },
        });
      }
    }

    // 1.2) Add AcademicYear Requirements
    const objects = selectedRequirements.map((req) => ({
      academic_year_id: academicYearId,
      requirement_id: req.requirement_id,
    }));
    await addAcademicYearRequirement({ variables: { objects } });

    // 2) Create Graduation Class with same start_date and academicYearId
    const gcName = academicYearName; // same base name
    const gcResult = await addGraduationClass({
      variables: {
        name: gcName,
        start_date: params.start_date,
        program_id: programId,
        current_academic_year_id: academicYearId,
      },
    });
    const graduationClassId = gcResult.data?.insert_graduation_class
      ?.returning?.[0]?.graduation_class_id as string | undefined;
    if (!graduationClassId) return;

    // 2.1) Add current user as GraduationClassStudent
    if (user?.user_id) {
      await addGraduationClassStudent({
        variables: {
          graduation_class_id: graduationClassId,
          student_id: user.user_id,
        },
      });
    }

    // 3) Add Mentoring (optional for educational organizations)
    for (const coach of selectedCoaches) {
      await addMentoring({
        variables: { mentee: user?.user_id, mentor: coach.user_id },
      });
    }

    nextStep();
  };

  return {
    // state
    state,
    loading,

    // requirements
    availableRequirements,
    selectedRequirements,
    handleAddRequirement,
    handleRemoveRequirement,

    // validation for step 1
    requirementsError,
    goNextFromRequirements,

    // tags
    tags,
    handleAddTag,
    handleRemoveTag,

    // coaches selection
    selectedCoaches,
    handleAddCoach,
    handleRemoveCoach,
    handleChangeCoaches,
    mentorsOptional,

    // nav
    nextStep,
    previousStep,
    goToStep,

    // complete
    handleComplete,

    // coaches
    coaches,
  };
};
